import type { DirectoryUser, MatterAssignee } from "@/lib/types";
import { formatUsersName } from "@/lib/utils/format";

export const TASK_MATTER_USERS_GROUP = "Assigned to this matter";
export const TASK_ALL_USERS_GROUP = "All users";

type TaskUserIdentity = Pick<
  DirectoryUser,
  "id" | "firstName" | "lastName" | "email" | "enabled"
>;

export type TaskUserOption = TaskUserIdentity & {
  group: typeof TASK_MATTER_USERS_GROUP | typeof TASK_ALL_USERS_GROUP;
  roleLabels: string[];
};

const getSortLabel = (option: TaskUserIdentity) =>
  (formatUsersName(option)?.trim() || option.email || option.id).trim();

const matchesSearch = (option: TaskUserOption, search: string) => {
  const normalizedSearch = search.trim().toLowerCase();
  if (!normalizedSearch) return true;

  return `${getSortLabel(option)} ${option.email} ${option.roleLabels.join(" ")}`
    .toLowerCase()
    .includes(normalizedSearch);
};

export const getMatterRoleLabelsByUserId = (
  matterAssignees: MatterAssignee[],
) => {
  const rolesByUserId = new Map<string, Set<string>>();

  matterAssignees.forEach((assignee) => {
    if (assignee.dateTimeEnd) return;

    const roles = rolesByUserId.get(assignee.user.id) ?? new Set<string>();
    roles.add(assignee.role.label);
    rolesByUserId.set(assignee.user.id, roles);
  });

  return new Map(
    [...rolesByUserId].map(([userId, roles]) => [
      userId,
      [...roles].sort((a, b) => a.localeCompare(b)),
    ]),
  );
};

export const buildTaskUserOptions = (
  matterAssignees: MatterAssignee[],
  directoryUsers: TaskUserIdentity[],
  search = "",
  selectedUserIds: string[] = [],
  userStatuses: Pick<DirectoryUser, "id" | "enabled">[] = directoryUsers,
) => {
  const selectedUserIdSet = new Set(selectedUserIds);
  const roleLabelsByUserId = getMatterRoleLabelsByUserId(matterAssignees);
  const userStatusesById = new Map(
    userStatuses.map((user) => [user.id, user.enabled]),
  );
  const matterUsers = new Map<string, TaskUserIdentity>();

  matterAssignees.forEach((assignee) => {
    if (
      assignee.dateTimeEnd ||
      matterUsers.has(assignee.user.id) ||
      ((userStatusesById.get(assignee.user.id) ?? assignee.user.enabled) ===
        false &&
        !selectedUserIdSet.has(assignee.user.id))
    ) {
      return;
    }

    matterUsers.set(assignee.user.id, assignee.user);
  });

  const assignedToMatter = [...matterUsers.values()]
    .map<TaskUserOption>((user) => ({
      ...user,
      group: TASK_MATTER_USERS_GROUP,
      roleLabels: roleLabelsByUserId.get(user.id) ?? [],
    }))
    .filter((option) => matchesSearch(option, search))
    .sort((a, b) => getSortLabel(a).localeCompare(getSortLabel(b)));

  const directoryUsersById = new Map(
    directoryUsers.map((user) => [user.id, user]),
  );
  const allUsers = [...directoryUsersById.values()]
    .filter(
      (user) =>
        !matterUsers.has(user.id) &&
        (user.enabled !== false || selectedUserIdSet.has(user.id)),
    )
    .map<TaskUserOption>((user) => ({
      ...user,
      group: TASK_ALL_USERS_GROUP,
      roleLabels: [],
    }))
    .filter((option) => matchesSearch(option, search))
    .sort((a, b) => getSortLabel(a).localeCompare(getSortLabel(b)));

  return [...assignedToMatter, ...allUsers];
};
